import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';

const { FiX, FiClock, FiUsers, FiTag, FiCalendar, FiCheckCircle, FiInfo, FiLayers, FiExternalLink } = FiIcons;

const EventDetailModal = ({ event, onClose, onBook }) => {
  if (!event) return null;

  const handleBooking = () => {
    if (event.booking_url) {
      window.open(event.booking_url, '_blank', 'noopener,noreferrer');
      onClose();
    } else {
      onBook(event);
    }
  };

  // Use parseISO to prevent timezone shifting of "YYYY-MM-DD" strings
  const displayDate = event.event_date ? format(parseISO(event.event_date), 'MMM d, yyyy') : '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 40 }}
        className="bg-white rounded-[2.5rem] overflow-hidden max-w-2xl w-full shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`relative h-56 p-10 text-white flex flex-col justify-end overflow-hidden bg-gradient-to-br ${event.event_type === 'workshop' ? 'from-indigo-600 to-purple-700' : 'from-rose-500 to-pink-600'}`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 bg-black/20 hover:bg-black/40 rounded-full backdrop-blur-md transition-all z-10"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
          <div className="relative z-10">
            <div className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] mb-4 backdrop-blur-sm border border-white/10">
              <SafeIcon icon={FiLayers} className="mr-2" />
              {event.event_type} Session
            </div>
            <h2 className="text-4xl font-bold leading-tight">{event.title}</h2>
          </div>
        </div>

        <div className="p-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: FiCalendar, label: 'Date', value: displayDate },
              { icon: FiClock, label: 'Time', value: event.start_time },
              { icon: FiUsers, label: 'Spots', value: `${event.spots_left}/${event.total_spots}` },
              { icon: FiTag, label: 'Price', value: event.price }
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <SafeIcon icon={stat.icon} className="w-5 h-5 text-rose-500 mb-2" />
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{stat.label}</span>
                <span className="text-sm font-bold text-gray-800">{stat.value}</span>
              </div>
            ))}
          </div>

          <div className="mb-10">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiInfo} className="mr-2 text-rose-500" />
              About this Session
            </h3>
            <div className="bg-rose-50/30 p-6 rounded-3xl border border-rose-100/50">
              <p className="text-gray-600 leading-relaxed whitespace-pre-line italic">
                {event.description || "In this session, we'll explore creativity and mindfulness. All materials provided."}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleBooking}
              className="flex-1 bg-gradient-to-r from-rose-500 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center space-x-2"
            >
              <span>{event.booking_url ? 'Book & Pay Now' : 'Reserve My Spot'}</span>
              <SafeIcon icon={event.booking_url ? FiExternalLink : FiCheckCircle} />
            </button>
            <button
              onClick={onClose}
              className="px-8 py-4 text-gray-500 font-bold hover:text-gray-800 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EventDetailModal;